import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTimes,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaEuroSign,
  FaExternalLinkAlt,
  FaStar,
  FaHourglassHalf,
  FaBan,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaBeer,
  FaUsers,
  FaFire
} from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import Header from './components/Header';
import { CALLMEBOT_CONFIG } from './config/navItems';
import { useLanguage } from './i18n';

const EventsPage = () => {
  const { t, tArray } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [showTunnelJamForm, setShowTunnelJamForm] = useState(false);
  const [showProFlyerForm, setShowProFlyerForm] = useState(false);

  // Tunnel Jam Form State
  const [tunnelJamFormData, setTunnelJamFormData] = useState({
    name: '',
    phone: '',
    email: '',
    flyingGroup: [],
    acceptsPaymentTerms: false,
    acceptsCompetencyDeclaration: false,
    experienceLevel: '',
    comments: ''
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  // ProFlyer Coaching Form State
  const [proFlyerFormData, setProFlyerFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experienceLevel: '',
    sessionDuration: '',
    sessionDurationOther: '',
    skills: [],
    skillsOther: '',
    comments: ''
  });

  const [proFlyerFormStatus, setProFlyerFormStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  useEffect(() => {
    // SEO optimized title for Tunnel Jam event
    document.title = "Wind Tunnel Events & Camps | Tunnel Jam MadridFly Feb 13 | Indoor Skydiving Training";

    // Add meta description for SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Join Tunnel Jam at MadridFly on February 13th - 30 minutes of group flying for 80EUR. Indoor skydiving events, tunnel camps, and professional wind tunnel coaching. All levels welcome.";
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowScrollHint(false);
    }, 5000);

    const scrollContainer = document.querySelector('.horizontal-scroll-container');
    const handleScroll = () => {
      setShowScrollHint(false);
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { once: true });
    }

    return () => {
      clearTimeout(timer);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      const scrollContainer = document.querySelector('.horizontal-scroll-container');
      if (scrollContainer && e.target.closest('.horizontal-scroll-wrapper')) {
        e.preventDefault();
        scrollContainer.scrollLeft += e.deltaY;
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  // Automatic date check: returns true if the event end date has passed or is today
  const isEventPast = (eventEndDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(eventEndDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate <= today;
  };

  // Tunnel Jam - New Featured Event
  const tunnelJamEvent = {
    id: 'tunnel-jam-feb',
    title: t('events.eventDetails.tunnelJam.title'),
    //subtitle: t('events.eventDetails.tunnelJam.subtitle'),
    //dates: t('events.eventDetails.tunnelJam.dates'),
    location: t('events.eventDetails.tunnelJam.location'),
    price: '80€',
    flightTime: '30 minutes',
    image: '/tunnel-jam-madridfly.jpg',
    badge: t('events.eventDetails.tunnelJam.badge'),
    isNew: false,
    isTunnelJam: true,
    endDate: 'Feb 13, 2036',
    isPast: isEventPast('Feb 13, 2036'),
    description: t('events.eventDetails.tunnelJam.description'),
    fullDescription: t('events.eventDetails.tunnelJam.fullDescription'),
    includes: tArray('events.eventDetails.tunnelJam.includes')
  };

  // ProFlyer Coaching Event
  const featuredEvent = {
    id: 'proflyer-coaching',
    title: t('events.eventDetails.proFlyer.title'),
    subtitle: t('events.eventDetails.proFlyer.subtitle'),
    dates: t('events.eventDetails.proFlyer.dates'),
    location: t('events.eventDetails.proFlyer.location'),
    price: '550€/hour',
    image: '/popup-desktop-bg.jpg',
    badge: t('events.eventDetails.proFlyer.badge'),
    isFeatured: true,
    isProFlyer: true,
    isPast: false,
    description: t('events.eventDetails.proFlyer.description'),
    fullDescription: t('events.eventDetails.proFlyer.fullDescription'),
    includes: tArray('events.eventDetails.proFlyer.includes')
  };

  const allOtherEvents = [
    {
      id: 'madrid-june',
      title: t('events.eventDetails.tunnelCampMadrid.title'),
      subtitle: 'Jun 25-29, 2025',
      location: t('events.eventDetails.tunnelCampMadrid.location'),
      price: '550€/hour',
      image: '/popup-mobile-bg.jpg',
      endDate: 'Jun 29, 2025',
      fullDescription: t('events.eventDetails.tunnelCampMadrid.fullDescription'),
      includes: tArray('events.eventDetails.tunnelCampMadrid.includes'),
      googleFormUrl: 'https://forms.gle/rz5cnUjtZ5SfQ6Qi7'
    },
    {
      id: 'madrid-july',
      title: t('events.eventDetails.tunnelCampMadrid.title'),
      subtitle: 'Jul 7-13, 2025',
      location: t('events.eventDetails.tunnelCampMadrid.location'),
      price: '550€/hour',
      image: '/cabecera.webp',
      endDate: 'Jul 13, 2025',
      fullDescription: t('events.eventDetails.tunnelCampMadrid.fullDescription'),
      includes: tArray('events.eventDetails.tunnelCampMadrid.includes'),
      googleFormUrl: 'https://forms.gle/rz5cnUjtZ5SfQ6Qi7'
    },
    {
      id: 'madrid-august',
      title: t('events.eventDetails.tunnelCampMadrid.title'),
      subtitle: 'Aug 4-10, 2025',
      location: t('events.eventDetails.tunnelCampMadrid.location'),
      price: '550€/hour',
      image: '/popup-mobile-bg.jpg',
      endDate: 'Aug 10, 2025',
      fullDescription: t('events.eventDetails.tunnelCampMadrid.fullDescription'),
      includes: tArray('events.eventDetails.tunnelCampMadrid.includes'),
      googleFormUrl: 'https://forms.gle/rz5cnUjtZ5SfQ6Qi7'
    },
    {
      id: 'finland-coming-soon',
      title: t('events.eventDetails.tunnelCampFinland.title'),
      subtitle: 'Aug 31-Sep 5, 2025',
      location: t('events.eventDetails.tunnelCampFinland.location'),
      price: '460€/hour',
      image: '/Aeronautica-Arena.jpg',
      isSoldOut: true,
      isComingSoon: false,
      endDate: 'Sep 5, 2025',
      fullDescription: t('events.eventDetails.tunnelCampFinland.fullDescription'),
      includes: tArray('events.eventDetails.tunnelCampFinland.includes'),
      googleFormUrl: 'https://forms.gle/XS5DbvMjU6snv1Ld6'
    }
  ];

  const currentEvents = allOtherEvents.filter(event => !isEventPast(event.endDate));
  const pastEvents = allOtherEvents.filter(event => isEventPast(event.endDate));

  // Past events sorted by most recent first (closest to today)
  const pastEventsWithFlag = pastEvents
    .map(event => ({ ...event, isPast: true }))
    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate));

  const otherEvents = [...currentEvents, ...pastEventsWithFlag];

  const eventPairs = [];
  for (let i = 0; i < otherEvents.length; i += 2) {
    eventPairs.push(otherEvents.slice(i, i + 2));
  }

  // Calculate where Tunnel Jam should be inserted among event pairs when closed
  // It should be placed in chronological order (most recent first) among closed events
  const tunnelJamInsertPairIndex = (() => {
    if (!tunnelJamEvent.isPast) return -1;
    // Find where TJ fits in the past events list (sorted most recent first)
    const tjDate = new Date(tunnelJamEvent.endDate);
    tjDate.setHours(0, 0, 0, 0);
    let posInPast = pastEventsWithFlag.findIndex(event => {
      const d = new Date(event.endDate);
      d.setHours(0, 0, 0, 0);
      return d <= tjDate;
    });
    if (posInPast === -1) posInPast = pastEventsWithFlag.length;
    // Total items before TJ = all current events + past events before TJ's position
    const itemsBefore = currentEvents.length + posInPast;
    // Convert to pair index (each pair has 2 events)
    return Math.ceil(itemsBefore / 2);
  })();

  const openEventModal = (event) => {
    // Always show the event details first
    setSelectedEvent(event);
  };

  const openEventForm = (event) => {
    if (event.isTunnelJam) {
      setSelectedEvent(null);
      setShowTunnelJamForm(true);
    } else if (event.isProFlyer) {
      setSelectedEvent(null);
      setShowProFlyerForm(true);
    }
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
  };

  const closeTunnelJamForm = () => {
    setShowTunnelJamForm(false);
    setFormStatus({ loading: false, success: false, error: false, message: '' });
    setTunnelJamFormData({
      name: '',
      phone: '',
      email: '',
      flyingGroup: [],
      acceptsPaymentTerms: false,
      acceptsCompetencyDeclaration: false,
      experienceLevel: '',
      comments: ''
    });
  };

  const openGoogleForm = (url) => {
    window.open(url, '_blank');
  };

  const handleFlyingGroupChange = (group) => {
    setTunnelJamFormData(prev => ({
      ...prev,
      flyingGroup: prev.flyingGroup.includes(group)
        ? prev.flyingGroup.filter(g => g !== group)
        : [...prev.flyingGroup, group]
    }));
  };

  const handleTunnelJamSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!tunnelJamFormData.name || !tunnelJamFormData.phone || !tunnelJamFormData.email) {
      setFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.requiredFields')
      });
      return;
    }

    if (tunnelJamFormData.flyingGroup.length === 0) {
      setFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.selectFlyingGroup')
      });
      return;
    }

    if (!tunnelJamFormData.acceptsPaymentTerms) {
      setFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.acceptPayment')
      });
      return;
    }

    if (!tunnelJamFormData.acceptsCompetencyDeclaration) {
      setFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.acceptCompetency')
      });
      return;
    }

    setFormStatus({
      loading: true,
      success: false,
      error: false,
      message: t('events.tunnelJam.form.sendingButton')
    });

    try {
      const serviceId = 'service_24z9to6';
      const clientTemplateId = 'template_iforiub';
      const adminTemplateId = 'template_b6143og';
      const publicKey = 'CqR0_WKL9ukUS9LaG';

      const timestamp = new Date().toLocaleString('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const templateParams = {
        from_name: tunnelJamFormData.name,
        from_email: tunnelJamFormData.email,
        phone: tunnelJamFormData.phone,
        message: `TUNNEL JAM REGISTRATION - February 13, 2025

Flying Groups: ${tunnelJamFormData.flyingGroup.join(', ')}
Experience Level: ${tunnelJamFormData.experienceLevel || 'Not specified'}

Payment Terms Accepted: Yes
Competency Declaration Accepted: Yes

Additional Comments: ${tunnelJamFormData.comments || 'None'}

---
Price: 80€
Flight Time: 30 minutes
Location: MadridFly, Madrid`,
        interest_level: tunnelJamFormData.experienceLevel || 'Tunnel Jam Participant',
        service_type: 'Tunnel Jam - February 13',
        preferred_contact: 'email',
        timestamp: timestamp,
        to_name: tunnelJamFormData.name,
        reply_to: tunnelJamFormData.email
      };

      // Send confirmation to user
      await emailjs.send(serviceId, clientTemplateId, templateParams, publicKey);

      // Send notification to admin/coach
      await emailjs.send(serviceId, adminTemplateId, {
        ...templateParams,
        to_email: 'juanjose@innovativecode.es'
      }, publicKey);

      // Send WhatsApp notification to coach via CallMeBot
      try {
        const whatsappMessage = `🎉 TUNNEL JAM REGISTRATION

👤 ${tunnelJamFormData.name}
📧 ${tunnelJamFormData.email}
📱 ${tunnelJamFormData.phone}
✈️ Groups: ${tunnelJamFormData.flyingGroup.join(', ')}
📅 Feb 13 - MadridFly`;

        const whatsappUrl = `https://api.callmebot.com/whatsapp.php?phone=${CALLMEBOT_CONFIG.phone}&text=${encodeURIComponent(whatsappMessage)}&apikey=${CALLMEBOT_CONFIG.apikey}`;

        // Fire and forget - don't wait for response
        fetch(whatsappUrl, { mode: 'no-cors' }).catch(() => {});
      } catch (whatsappError) {
        // Silent fail for WhatsApp - email is the primary notification
        console.log('WhatsApp notification skipped');
      }

      setFormStatus({
        loading: false,
        success: true,
        error: false,
        message: t('events.formSuccess.tunnelJam')
      });

      // Reset form
      setTunnelJamFormData({
        name: '',
        phone: '',
        email: '',
        flyingGroup: [],
        acceptsPaymentTerms: false,
        acceptsCompetencyDeclaration: false,
        experienceLevel: '',
        comments: ''
      });

    } catch (error) {
      console.error('EmailJS Error:', error);
      setFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.sendFailed')
      });
    }
  };

  // ProFlyer Form Functions
  const closeProFlyerForm = () => {
    setShowProFlyerForm(false);
    setProFlyerFormStatus({ loading: false, success: false, error: false, message: '' });
    setProFlyerFormData({
      name: '',
      email: '',
      phone: '',
      experienceLevel: '',
      sessionDuration: '',
      sessionDurationOther: '',
      skills: [],
      skillsOther: '',
      comments: ''
    });
  };

  const handleSkillsChange = (skill) => {
    setProFlyerFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleProFlyerSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!proFlyerFormData.name || !proFlyerFormData.email || !proFlyerFormData.phone) {
      setProFlyerFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.requiredFields')
      });
      return;
    }

    if (!proFlyerFormData.experienceLevel) {
      setProFlyerFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.selectExperience')
      });
      return;
    }

    if (!proFlyerFormData.sessionDuration) {
      setProFlyerFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.selectDuration')
      });
      return;
    }

    if (proFlyerFormData.skills.length === 0) {
      setProFlyerFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.selectSkills')
      });
      return;
    }

    setProFlyerFormStatus({
      loading: true,
      success: false,
      error: false,
      message: t('events.proFlyer.form.sendingButton')
    });

    try {
      const serviceId = 'service_24z9to6';
      const clientTemplateId = 'template_iforiub';
      const adminTemplateId = 'template_b6143og';
      const publicKey = 'CqR0_WKL9ukUS9LaG';

      const timestamp = new Date().toLocaleString('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const sessionDurationText = proFlyerFormData.sessionDuration === 'Other'
        ? proFlyerFormData.sessionDurationOther
        : proFlyerFormData.sessionDuration;

      const skillsList = [...proFlyerFormData.skills];
      if (proFlyerFormData.skills.includes('Other') && proFlyerFormData.skillsOther) {
        const otherIndex = skillsList.indexOf('Other');
        skillsList[otherIndex] = `Other: ${proFlyerFormData.skillsOther}`;
      }

      const templateParams = {
        from_name: proFlyerFormData.name,
        from_email: proFlyerFormData.email,
        phone: proFlyerFormData.phone,
        message: `PROFLYER COACHING REQUEST

Experience Level: ${proFlyerFormData.experienceLevel}
Session Duration: ${sessionDurationText}

Skills to Focus On:
${skillsList.map(s => `• ${s}`).join('\n')}

Additional Comments: ${proFlyerFormData.comments || 'None'}

---
Service: ProFlyer Coaching (1-on-1 Wind Tunnel Session)
Price: 550€/hour
Location: Flexible - Any Tunnel Worldwide`,
        interest_level: proFlyerFormData.experienceLevel,
        service_type: 'ProFlyer Coaching - 1-on-1 Session',
        preferred_contact: 'email',
        timestamp: timestamp,
        to_name: proFlyerFormData.name,
        reply_to: proFlyerFormData.email
      };

      // Send confirmation to user
      await emailjs.send(serviceId, clientTemplateId, templateParams, publicKey);

      // Send notification to admin/coach
      await emailjs.send(serviceId, adminTemplateId, {
        ...templateParams,
        to_email: 'juanjose@innovativecode.es'
      }, publicKey);

      // Send WhatsApp notification to coach via CallMeBot
      try {
        const whatsappMessage = `🆕 PROFLYER COACHING REQUEST

👤 ${proFlyerFormData.name}
📧 ${proFlyerFormData.email}
📱 ${proFlyerFormData.phone}
📊 Level: ${proFlyerFormData.experienceLevel}
⏱️ Duration: ${sessionDurationText}
🎯 Skills: ${skillsList.join(', ')}`;

        const whatsappUrl = `https://api.callmebot.com/whatsapp.php?phone=${CALLMEBOT_CONFIG.phone}&text=${encodeURIComponent(whatsappMessage)}&apikey=${CALLMEBOT_CONFIG.apikey}`;

        // Fire and forget - don't wait for response
        fetch(whatsappUrl, { mode: 'no-cors' }).catch(() => {});
      } catch (whatsappError) {
        // Silent fail for WhatsApp - email is the primary notification
        console.log('WhatsApp notification skipped');
      }

      setProFlyerFormStatus({
        loading: false,
        success: true,
        error: false,
        message: t('events.formSuccess.proFlyer')
      });

      // Reset form
      setProFlyerFormData({
        name: '',
        email: '',
        phone: '',
        experienceLevel: '',
        sessionDuration: '',
        sessionDurationOther: '',
        skills: [],
        skillsOther: '',
        comments: ''
      });

    } catch (error) {
      console.error('EmailJS Error:', error);
      setProFlyerFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('events.formErrors.sendFailed')
      });
    }
  };

  // Get translated arrays for form options
  const flyingGroups = tArray('events.tunnelJam.form.flyingGroups');
  const tunnelJamExperienceLevels = tArray('events.tunnelJam.form.experienceLevels');
  const proFlyerExperienceLevels = tArray('events.proFlyer.form.experienceLevels');
  const sessionDurations = tArray('events.proFlyer.form.sessionDurations');
  const skillsOptions = tArray('events.proFlyer.form.skills');

  // Tunnel Jam section - rendered conditionally before or after other events
  const tunnelJamSection = (
    <div className="flex-shrink-0 relative w-full md:w-[50vw] md:min-w-[50vw]">
      <div
        className="h-full bg-cover bg-center relative cursor-pointer group"
        style={{
          backgroundImage: `url(${tunnelJamEvent.image})`,
          backgroundColor: '#1e3a5f'
        }}
        onClick={() => openEventModal(tunnelJamEvent)}
      >
        {/* Overlay with gradient */}
        <div className={`absolute inset-0 bg-gradient-to-b ${tunnelJamEvent.isPast ? 'from-black/70 via-blue-900/60 to-black/85' : 'from-black/50 via-blue-900/40 to-black/70 group-hover:from-black/60 group-hover:via-blue-900/50 group-hover:to-black/80'} transition-all duration-300`}></div>

        {/* CLOSED overlay for past event */}
        {tunnelJamEvent.isPast && (
          <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
            <div className="relative transform -rotate-12" style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))' }}>
              <div className="relative border-4 border-red-500 bg-red-500/20 backdrop-blur-sm px-8 py-4 rounded-lg">
                <div className="absolute inset-2 border-2 border-red-500 rounded"></div>
                <div className="relative z-10">
                  <div className="text-red-500 font-black text-2xl md:text-4xl tracking-widest text-center">{t('events.otherEvents.closed')}</div>
                  <div className="text-red-400 font-bold text-xs md:text-sm tracking-wider text-center mt-1">{t('events.otherEvents.eventEnded')}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-12 text-white max-w-xl">
          {/* Badge - conditional on past status */}
          {!tunnelJamEvent.isPast ? (
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 w-fit shadow-lg shadow-green-500/50"
            >
              <FaFire className="inline mr-2" />
              {t('events.tunnelJam.badge')}
            </motion.div>
          ) : (
            <div className="inline-block bg-gray-600 text-gray-300 px-4 py-2 rounded-full text-sm font-bold mb-6 w-fit">
              <FaBan className="inline mr-2" />
              {t('events.otherEvents.eventEnded')}
            </div>
          )}

          {/* Title */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-2 leading-tight">
            <span className={`bg-gradient-to-r ${tunnelJamEvent.isPast ? 'from-gray-400 to-gray-500' : 'from-orange-500 to-red-500'} bg-clip-text text-transparent`}>
              TUNNEL
            </span>
            <br />
            <span className={`bg-gradient-to-r ${tunnelJamEvent.isPast ? 'from-gray-400 to-gray-500' : 'from-orange-500 to-red-500'} bg-clip-text text-transparent`}>
              JAM
            </span>
          </h1>

          {/* Location */}
          <h2 className={`text-2xl md:text-4xl font-black ${tunnelJamEvent.isPast ? 'text-gray-400' : 'text-blue-400'} mb-4`}>
            {t('events.tunnelJam.location')}
          </h2>

          {/* Date */}
          <div className={`flex items-center text-lg md:text-xl ${tunnelJamEvent.isPast ? 'text-gray-400' : 'text-white/90'} mb-6`}>
            <FaCalendarAlt className={`mr-3 ${tunnelJamEvent.isPast ? 'text-gray-500' : 'text-orange-500'}`} />
            <span className="font-bold">{t('events.tunnelJam.date')}</span>
          </div>

          {/* Key Info Cards */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className={`bg-black/40 backdrop-blur-sm rounded-xl p-3 border ${tunnelJamEvent.isPast ? 'border-gray-600' : 'border-orange-500/30'}`}>
              <div className="flex items-center text-white">
                <FaEuroSign className={`${tunnelJamEvent.isPast ? 'text-gray-500' : 'text-orange-500'} mr-2`} />
                <div>
                  <div className="text-xs text-gray-400">{t('events.tunnelJam.price')}</div>
                  <div className={`text-lg font-bold ${tunnelJamEvent.isPast ? 'text-gray-300' : ''}`}>80€</div>
                </div>
              </div>
            </div>
            <div className={`bg-black/40 backdrop-blur-sm rounded-xl p-3 border ${tunnelJamEvent.isPast ? 'border-gray-600' : 'border-orange-500/30'}`}>
              <div className="flex items-center text-white">
                <FaClock className={`${tunnelJamEvent.isPast ? 'text-gray-500' : 'text-orange-500'} mr-2`} />
                <div>
                  <div className="text-xs text-gray-400">{t('events.tunnelJam.flightTime')}</div>
                  <div className={`text-lg font-bold ${tunnelJamEvent.isPast ? 'text-gray-300' : ''}`}>30 min</div>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-2 mb-6">
            <div className={`flex items-center text-sm ${tunnelJamEvent.isPast ? 'text-gray-400' : 'text-white/90'}`}>
              <FaUsers className={`${tunnelJamEvent.isPast ? 'text-gray-500' : 'text-orange-500'} mr-3 w-4`} />
              <span>{t('events.tunnelJam.features.groups')}</span>
            </div>
            <div className={`flex items-center text-sm ${tunnelJamEvent.isPast ? 'text-gray-400' : 'text-white/90'}`}>
              <FaBeer className={`${tunnelJamEvent.isPast ? 'text-gray-500' : 'text-orange-500'} mr-3 w-4`} />
              <span>{t('events.tunnelJam.features.snacks')}</span>
            </div>
            <div className={`flex items-center text-sm ${tunnelJamEvent.isPast ? 'text-gray-400' : 'text-white/90'}`}>
              <FaStar className={`${tunnelJamEvent.isPast ? 'text-gray-500' : 'text-orange-500'} mr-3 w-4`} />
              <span>{t('events.tunnelJam.features.levels')}</span>
            </div>
          </div>

          {/* CTA */}
          {!tunnelJamEvent.isPast ? (
            <motion.div
              className="text-orange-400 text-base font-bold"
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {t('events.tunnelJam.clickToRegister')} →
            </motion.div>
          ) : (
            <div className="text-gray-400 text-base font-bold">
              {t('events.otherEvents.viewDetails')} →
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900 overflow-hidden">
      {/* Header Component */}
      <Header />

      {/* Main Content - Full screen, no vertical scroll */}
      <div className="absolute inset-0 pt-16 md:pt-20 overflow-hidden horizontal-scroll-wrapper">
        <div className="h-full overflow-x-auto overflow-y-hidden horizontal-scroll-container">
          <style>{`
            .horizontal-scroll-container::-webkit-scrollbar {
              height: 8px;
            }
            .horizontal-scroll-container::-webkit-scrollbar-track {
              background: rgba(0,0,0,0.3);
            }
            .horizontal-scroll-container::-webkit-scrollbar-thumb {
              background: linear-gradient(to right, #f97316, #ef4444);
              border-radius: 4px;
            }
            @media (max-width: 768px) {
              .horizontal-scroll-container::-webkit-scrollbar {
                display: none;
              }
              .horizontal-scroll-container {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
            }
          `}</style>

          {/* Horizontal scrolling container */}
          <div className="flex h-full">

            {/* TUNNEL JAM - Show first (left) when active */}
            {!tunnelJamEvent.isPast && tunnelJamSection}

            {/* ProFlyer Coaching - Takes 50vw */}
            <div className="flex-shrink-0 relative w-full md:w-[50vw] md:min-w-[50vw]">
              <div
                className="h-full bg-cover bg-center relative cursor-pointer group"
                style={{ backgroundImage: `url(${featuredEvent.image})` }}
                onClick={() => openEventModal(featuredEvent)}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80 group-hover:from-black/70 group-hover:via-black/50 group-hover:to-black/90 transition-all duration-300"></div>

                <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-12 text-white max-w-xl">
                  <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 w-fit">
                    <FaStar className="inline mr-2" />
                    {t('events.proFlyer.badge')}
                  </div>

                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight">
                    <span className="bg-gradient-to-r from-lime-400 to-green-400 bg-clip-text text-transparent">
                      {t('events.proFlyer.title')}
                    </span>
                    <br />
                    <span className="text-white">
                      {t('events.proFlyer.subtitle')}
                    </span>
                  </h1>

                  <h2 className="text-lg md:text-xl font-bold text-orange-400 mb-2">
                    {t('events.proFlyer.location')}
                  </h2>

                  <h3 className="text-base md:text-lg text-white/90 mb-6">
                    {t('events.proFlyer.schedule')}
                  </h3>

                  <div className="bg-black/50 backdrop-blur-sm rounded-xl p-4 mb-6 w-fit border border-orange-500/20">
                    <div className="flex items-center text-white">
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-full p-3 mr-3">
                        <FaEuroSign className="text-white text-base" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-300">{t('events.proFlyer.investment')}</div>
                        <div className="text-xl font-bold">{featuredEvent.price}</div>
                        <div className="text-xs text-gray-400">{t('events.proFlyer.priceNote')}</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    <div className="flex items-center text-sm text-white/90">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                      <span>{t('events.proFlyer.features.anyTunnel')}</span>
                    </div>
                    <div className="flex items-center text-sm text-white/90">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                      <span>{t('events.proFlyer.features.customSchedule')}</span>
                    </div>
                    <div className="flex items-center text-sm text-white/90">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                      <span>{t('events.proFlyer.features.oneOnOne')}</span>
                    </div>
                  </div>

                  <div className="text-orange-400 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {t('events.proFlyer.clickToLearnMore')} →
                  </div>
                </div>
              </div>
            </div>

            {/* Event pairs with Tunnel Jam inserted at correct chronological position */}
            {eventPairs.map((pair, pairIndex) => (
              <React.Fragment key={pairIndex}>
                {/* Insert closed Tunnel Jam before this pair if this is its chronological position */}
                {tunnelJamEvent.isPast && pairIndex === tunnelJamInsertPairIndex && tunnelJamSection}

                <div className="flex-shrink-0 flex flex-col w-full md:w-[50vw] md:min-w-[50vw]">
                  {pair.map((event) => (
                    <div
                      key={event.id}
                      className="flex-1 relative cursor-pointer group border-b border-gray-300/10 last:border-b-0"
                      onClick={() => openEventModal(event)}
                    >
                      <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${event.image})` }}
                      >
                        <div className={`absolute inset-0 ${event.isPast ? 'bg-black/70' : 'bg-black/50'} group-hover:bg-black/60 transition-all duration-300`}></div>
                      </div>

                      {event.isPast && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                          <div className="relative transform -rotate-12" style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))' }}>
                            <div className="relative border-4 border-red-500 bg-red-500/20 backdrop-blur-sm px-8 py-4 rounded-lg">
                              <div className="absolute inset-2 border-2 border-red-500 rounded"></div>
                              <div className="relative z-10">
                                <div className="text-red-500 font-black text-2xl md:text-4xl tracking-widest text-center">{t('events.otherEvents.closed')}</div>
                                <div className="text-red-400 font-bold text-xs md:text-sm tracking-wider text-center mt-1">{t('events.otherEvents.eventEnded')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {event.isSoldOut && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                          <div className="relative transform -rotate-12" style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))' }}>
                            <div className="relative border-4 border-yellow-500 bg-yellow-500/20 backdrop-blur-sm px-8 py-4 rounded-lg">
                              <div className="absolute inset-2 border-2 border-yellow-500 rounded"></div>
                              <div className="relative z-10">
                                <div className="text-yellow-500 font-black text-2xl md:text-4xl tracking-widest text-center">{t('events.otherEvents.soldOut')}</div>
                                <div className="text-yellow-400 font-bold text-xs md:text-sm tracking-wider text-center mt-1">{t('events.otherEvents.eventFull')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {event.isComingSoon && !event.isPast && (
                        <div className="absolute top-4 left-4 z-20 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                          <FaHourglassHalf className="inline mr-1" />
                          {t('events.otherEvents.comingSoon')}
                        </div>
                      )}

                      <div className="relative z-10 h-full flex items-center justify-between p-6 text-white">
                        <div className="flex-1">
                          <h3 className={`text-lg md:text-xl font-black mb-1 leading-tight ${event.isPast ? 'text-gray-300' : 'text-white'}`}>
                            {event.title}
                          </h3>
                          <h4 className={`text-sm md:text-base font-bold mb-2 ${event.isPast ? 'text-gray-400' : 'text-orange-400'}`}>
                            {event.subtitle}
                          </h4>
                          <div className={`text-xs mb-2 flex items-center ${event.isPast ? 'text-gray-400' : 'text-white/80'}`}>
                            <FaMapMarkerAlt className="mr-1" />
                            {event.location}
                          </div>
                          <div className={`text-sm font-bold ${event.isPast ? 'text-gray-300' : 'text-white'}`}>
                            {event.price}
                          </div>

                          <div className={`text-xs font-semibold mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${event.isPast ? 'text-gray-400' : 'text-orange-400'}`}>
                            {event.isPast ? t('events.otherEvents.viewDetails') : event.isComingSoon ? t('events.otherEvents.getNotified') : t('events.otherEvents.registerNow')} →
                          </div>
                        </div>

                        <div className="flex-shrink-0 ml-4">
                          <div className={`w-12 h-12 ${event.isPast ? 'bg-gray-600' : 'bg-gradient-to-r from-orange-500 to-red-500'} rounded-full flex items-center justify-center`}>
                            {event.isPast ? (
                              <FaBan className="text-white text-sm" />
                            ) : event.isComingSoon ? (
                              <FaHourglassHalf className="text-white text-sm" />
                            ) : (
                              <FaCalendarAlt className="text-white text-sm" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </React.Fragment>
            ))}

            {/* Insert closed Tunnel Jam after all pairs if it's the oldest */}
            {tunnelJamEvent.isPast && tunnelJamInsertPairIndex >= eventPairs.length && tunnelJamSection}
          </div>
        </div>
      </div>

      {/* Scroll hint for mobile */}
      {showScrollHint && (
        <div className="md:hidden fixed bottom-4 left-1/2 transform -translate-x-1/2 z-30">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 flex items-center space-x-3"
          >
            <div className="flex items-center text-white text-sm">
              <span className="mr-2">{t('events.scrollHint')}</span>
              <motion.div
                animate={{ x: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="flex items-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.59Z"/>
                </svg>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Tunnel Jam Registration Form Modal */}
      <AnimatePresence>
        {showTunnelJamForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative max-w-xl w-full bg-gradient-to-b from-gray-900 to-blue-900 rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Close button */}
              <button
                onClick={closeTunnelJamForm}
                className="absolute top-4 right-4 z-20 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
                aria-label="Close form"
              >
                <FaTimes />
              </button>

              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
                <div className="flex items-center mb-2">
                  <FaFire className="mr-2" />
                  <span className="text-sm font-bold bg-white/20 px-2 py-1 rounded">{t('events.tunnelJam.badge')}</span>
                </div>
                <h2 className="text-3xl font-black">{t('events.tunnelJam.form.title')}</h2>
                <p className="text-white/90">{t('events.tunnelJam.form.subtitle')} • {t('events.tunnelJam.date')}</p>
                <div className="flex items-center mt-3 text-sm">
                  <FaMapMarkerAlt className="mr-2" />
                  <span>{t('events.tunnelJam.location')}, Madrid</span>
                  <span className="mx-3">|</span>
                  <FaEuroSign className="mr-1" />
                  <span>80€</span>
                  <span className="mx-3">|</span>
                  <FaClock className="mr-1" />
                  <span>30 min</span>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6">
                {/* Status Messages */}
                <AnimatePresence>
                  {(formStatus.success || formStatus.error) && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`p-4 rounded-lg mb-6 flex items-start ${
                        formStatus.success
                          ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                          : 'bg-red-500/20 border border-red-500/30 text-red-300'
                      }`}
                    >
                      {formStatus.success ? (
                        <FaCheckCircle className="mr-3 mt-0.5 flex-shrink-0" />
                      ) : (
                        <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
                      )}
                      <span>{formStatus.message}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!formStatus.success && (
                  <form onSubmit={handleTunnelJamSubmit} className="space-y-5">
                    {/* Name */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.tunnelJam.form.nameLabel')} <span className="text-red-400">{t('events.tunnelJam.form.required')}</span>
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                        placeholder={t('events.tunnelJam.form.namePlaceholder')}
                        value={tunnelJamFormData.name}
                        onChange={(e) => setTunnelJamFormData({...tunnelJamFormData, name: e.target.value})}
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.tunnelJam.form.phoneLabel')} <span className="text-red-400">{t('events.tunnelJam.form.required')}</span>
                      </label>
                      <input
                        type="tel"
                        required
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                        placeholder={t('events.tunnelJam.form.phonePlaceholder')}
                        value={tunnelJamFormData.phone}
                        onChange={(e) => setTunnelJamFormData({...tunnelJamFormData, phone: e.target.value})}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.tunnelJam.form.emailLabel')} <span className="text-red-400">{t('events.tunnelJam.form.required')}</span>
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                        placeholder={t('events.tunnelJam.form.emailPlaceholder')}
                        value={tunnelJamFormData.email}
                        onChange={(e) => setTunnelJamFormData({...tunnelJamFormData, email: e.target.value})}
                      />
                    </div>

                    {/* Flying Group Selection */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.tunnelJam.form.flyingGroupLabel')} <span className="text-red-400">{t('events.tunnelJam.form.required')}</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {flyingGroups.map((group) => (
                          <label
                            key={group}
                            className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                              tunnelJamFormData.flyingGroup.includes(group)
                                ? 'bg-orange-500/30 border-2 border-orange-500'
                                : 'bg-gray-700/50 border-2 border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={tunnelJamFormData.flyingGroup.includes(group)}
                              onChange={() => handleFlyingGroupChange(group)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                              tunnelJamFormData.flyingGroup.includes(group)
                                ? 'bg-orange-500 border-orange-500'
                                : 'border-gray-500'
                            }`}>
                              {tunnelJamFormData.flyingGroup.includes(group) && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <span className="text-white font-medium">{group}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Experience Level (Added for better context) */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.tunnelJam.form.experienceLabel')} <span className="text-gray-400">({t('events.tunnelJam.form.optional')})</span>
                      </label>
                      <select
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white"
                        value={tunnelJamFormData.experienceLevel}
                        onChange={(e) => setTunnelJamFormData({...tunnelJamFormData, experienceLevel: e.target.value})}
                      >
                        <option value="">{t('events.tunnelJam.form.experiencePlaceholder')}</option>
                        {tunnelJamExperienceLevels.map((level) => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">{t('events.tunnelJam.form.experienceNote')}</p>
                    </div>

                    {/* Payment Terms Checkbox */}
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tunnelJamFormData.acceptsPaymentTerms}
                          onChange={(e) => setTunnelJamFormData({...tunnelJamFormData, acceptsPaymentTerms: e.target.checked})}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                          tunnelJamFormData.acceptsPaymentTerms
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-yellow-500'
                        }`}>
                          {tunnelJamFormData.acceptsPaymentTerms && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className="text-yellow-200 text-sm">
                          <span className="text-red-400">{t('events.tunnelJam.form.required')}</span> {t('events.tunnelJam.form.paymentTerms')}
                        </span>
                      </label>
                    </div>

                    {/* Competency Declaration Checkbox */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tunnelJamFormData.acceptsCompetencyDeclaration}
                          onChange={(e) => setTunnelJamFormData({...tunnelJamFormData, acceptsCompetencyDeclaration: e.target.checked})}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                          tunnelJamFormData.acceptsCompetencyDeclaration
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-blue-500'
                        }`}>
                          {tunnelJamFormData.acceptsCompetencyDeclaration && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className="text-blue-200 text-sm">
                          <span className="text-red-400">{t('events.tunnelJam.form.required')}</span> {t('events.tunnelJam.form.competencyDeclaration')}
                        </span>
                      </label>
                    </div>

                    {/* Additional Comments */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.tunnelJam.form.commentsLabel')} <span className="text-gray-400">({t('events.tunnelJam.form.optional')})</span>
                      </label>
                      <textarea
                        rows="3"
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                        placeholder={t('events.tunnelJam.form.commentsPlaceholder')}
                        value={tunnelJamFormData.comments}
                        onChange={(e) => setTunnelJamFormData({...tunnelJamFormData, comments: e.target.value})}
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={formStatus.loading}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-orange-500/30 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                    >
                      {formStatus.loading ? (
                        <>
                          <FaSpinner className="animate-spin mr-3" />
                          {t('events.tunnelJam.form.sendingButton')}
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="mr-3" />
                          {t('events.tunnelJam.form.submitButton')}
                        </>
                      )}
                    </button>
                  </form>
                )}

                {formStatus.success && (
                  <div className="text-center py-6">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                      <FaCheckCircle className="text-white text-4xl" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-white mb-2">{t('events.tunnelJam.form.successTitle')}</h3>
                    <p className="text-gray-300 mb-6">{t('events.tunnelJam.form.successMessage')}</p>
                    <button
                      onClick={closeTunnelJamForm}
                      className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-8 rounded-xl font-bold hover:shadow-lg transition-all"
                    >
                      {t('events.tunnelJam.form.closeButton')}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ProFlyer Coaching Registration Form Modal */}
      <AnimatePresence>
        {showProFlyerForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative max-w-xl w-full bg-gradient-to-b from-gray-900 to-blue-900 rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Close button */}
              <button
                onClick={closeProFlyerForm}
                className="absolute top-4 right-4 z-20 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
                aria-label="Close form"
              >
                <FaTimes />
              </button>

              {/* Header */}
              <div className="bg-gradient-to-r from-lime-500 to-green-500 p-6 text-white">
                <div className="flex items-center mb-2">
                  <FaStar className="mr-2" />
                  <span className="text-sm font-bold bg-white/20 px-2 py-1 rounded">{t('events.proFlyer.badge')}</span>
                </div>
                <h2 className="text-3xl font-black">{t('events.proFlyer.form.title')}</h2>
                <p className="text-white/90">{t('events.proFlyer.form.subtitle')}</p>
                <div className="flex items-center mt-3 text-sm">
                  <FaMapMarkerAlt className="mr-2" />
                  <span>{t('events.proFlyer.features.anyTunnel')}</span>
                  <span className="mx-3">|</span>
                  <FaEuroSign className="mr-1" />
                  <span>550€/hour</span>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6">
                {/* Status Messages */}
                <AnimatePresence>
                  {(proFlyerFormStatus.success || proFlyerFormStatus.error) && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`p-4 rounded-lg mb-6 flex items-start ${
                        proFlyerFormStatus.success
                          ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                          : 'bg-red-500/20 border border-red-500/30 text-red-300'
                      }`}
                    >
                      {proFlyerFormStatus.success ? (
                        <FaCheckCircle className="mr-3 mt-0.5 flex-shrink-0" />
                      ) : (
                        <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
                      )}
                      <span>{proFlyerFormStatus.message}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!proFlyerFormStatus.success && (
                  <form onSubmit={handleProFlyerSubmit} className="space-y-5">
                    {/* Name */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.proFlyer.form.nameLabel')} <span className="text-red-400">{t('common.required')}</span>
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 text-white placeholder-gray-400"
                        placeholder={t('events.proFlyer.form.namePlaceholder')}
                        value={proFlyerFormData.name}
                        onChange={(e) => setProFlyerFormData({...proFlyerFormData, name: e.target.value})}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.proFlyer.form.emailLabel')} <span className="text-red-400">{t('common.required')}</span>
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 text-white placeholder-gray-400"
                        placeholder={t('events.proFlyer.form.emailPlaceholder')}
                        value={proFlyerFormData.email}
                        onChange={(e) => setProFlyerFormData({...proFlyerFormData, email: e.target.value})}
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.proFlyer.form.phoneLabel')} <span className="text-red-400">{t('common.required')}</span>
                      </label>
                      <input
                        type="tel"
                        required
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 text-white placeholder-gray-400"
                        placeholder={t('events.proFlyer.form.phonePlaceholder')}
                        value={proFlyerFormData.phone}
                        onChange={(e) => setProFlyerFormData({...proFlyerFormData, phone: e.target.value})}
                      />
                    </div>

                    {/* Experience Level */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.proFlyer.form.experienceLabel')} <span className="text-red-400">{t('common.required')}</span>
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {proFlyerExperienceLevels.map((level) => (
                          <label
                            key={level}
                            className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition-all text-center ${
                              proFlyerFormData.experienceLevel === level
                                ? 'bg-lime-500/30 border-2 border-lime-500'
                                : 'bg-gray-700/50 border-2 border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <input
                              type="radio"
                              name="experienceLevel"
                              value={level}
                              checked={proFlyerFormData.experienceLevel === level}
                              onChange={(e) => setProFlyerFormData({...proFlyerFormData, experienceLevel: e.target.value})}
                              className="sr-only"
                            />
                            <span className="text-white font-medium">{level}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Session Duration */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.proFlyer.form.sessionDurationLabel')} <span className="text-red-400">{t('common.required')}</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {sessionDurations.map((duration) => (
                          <label
                            key={duration}
                            className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition-all text-center ${
                              proFlyerFormData.sessionDuration === duration
                                ? 'bg-lime-500/30 border-2 border-lime-500'
                                : 'bg-gray-700/50 border-2 border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <input
                              type="radio"
                              name="sessionDuration"
                              value={duration}
                              checked={proFlyerFormData.sessionDuration === duration}
                              onChange={(e) => setProFlyerFormData({...proFlyerFormData, sessionDuration: e.target.value})}
                              className="sr-only"
                            />
                            <span className="text-white font-medium">{duration}</span>
                          </label>
                        ))}
                      </div>
                      {proFlyerFormData.sessionDuration === 'Other' && (
                        <input
                          type="text"
                          className="w-full p-3 mt-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 text-white placeholder-gray-400"
                          placeholder={t('events.proFlyer.form.specifyDuration')}
                          value={proFlyerFormData.sessionDurationOther}
                          onChange={(e) => setProFlyerFormData({...proFlyerFormData, sessionDurationOther: e.target.value})}
                        />
                      )}
                    </div>

                    {/* Skills to Focus On */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.proFlyer.form.skillsLabel')} <span className="text-red-400">{t('common.required')}</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {skillsOptions.map((skill) => (
                          <label
                            key={skill}
                            className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                              proFlyerFormData.skills.includes(skill)
                                ? 'bg-lime-500/30 border-2 border-lime-500'
                                : 'bg-gray-700/50 border-2 border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={proFlyerFormData.skills.includes(skill)}
                              onChange={() => handleSkillsChange(skill)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                              proFlyerFormData.skills.includes(skill)
                                ? 'bg-lime-500 border-lime-500'
                                : 'border-gray-500'
                            }`}>
                              {proFlyerFormData.skills.includes(skill) && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <span className="text-white font-medium text-sm">{skill}</span>
                          </label>
                        ))}
                      </div>
                      {proFlyerFormData.skills.includes('Other') && (
                        <input
                          type="text"
                          className="w-full p-3 mt-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 text-white placeholder-gray-400"
                          placeholder={t('events.proFlyer.form.specifySkills')}
                          value={proFlyerFormData.skillsOther}
                          onChange={(e) => setProFlyerFormData({...proFlyerFormData, skillsOther: e.target.value})}
                        />
                      )}
                    </div>

                    {/* Additional Comments */}
                    <div>
                      <label className="block mb-2 text-white font-medium">
                        {t('events.proFlyer.form.commentsLabel')} <span className="text-gray-400">{t('common.optional')}</span>
                      </label>
                      <textarea
                        rows="3"
                        className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 text-white placeholder-gray-400"
                        placeholder={t('events.proFlyer.form.commentsPlaceholder')}
                        value={proFlyerFormData.comments}
                        onChange={(e) => setProFlyerFormData({...proFlyerFormData, comments: e.target.value})}
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={proFlyerFormStatus.loading}
                      className="w-full bg-gradient-to-r from-lime-500 to-green-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-lime-500/30 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                    >
                      {proFlyerFormStatus.loading ? (
                        <>
                          <FaSpinner className="animate-spin mr-3" />
                          {t('events.proFlyer.form.sendingButton')}
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="mr-3" />
                          {t('events.proFlyer.form.submitButton')}
                        </>
                      )}
                    </button>
                  </form>
                )}

                {proFlyerFormStatus.success && (
                  <div className="text-center py-6">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                      <FaCheckCircle className="text-white text-4xl" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-white mb-2">{t('events.proFlyer.form.successTitle')}</h3>
                    <p className="text-gray-300 mb-6">{t('events.proFlyer.form.successMessage')}</p>
                    <button
                      onClick={closeProFlyerForm}
                      className="bg-gradient-to-r from-lime-500 to-green-500 text-white py-3 px-8 rounded-xl font-bold hover:shadow-lg transition-all"
                    >
                      {t('common.close')}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Standard Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative max-w-2xl w-full bg-gradient-to-b from-gray-900 to-blue-900 rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={closeEventModal}
                className="absolute top-4 right-4 z-20 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
                aria-label="Close modal"
              >
                <FaTimes />
              </button>

              <div
                className="h-48 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${selectedEvent.image})` }}
              >
                <div className={`absolute inset-0 ${selectedEvent.isPast ? 'bg-black/70' : 'bg-black/50'}`}></div>

                {selectedEvent.isFeatured && (
                  <div className="absolute top-4 left-4 bg-orange-500 text-black px-3 py-1 rounded-full text-sm font-bold">
                    <FaStar className="inline mr-1" />
                    FEATURED EVENT
                  </div>
                )}

                {selectedEvent.isComingSoon && !selectedEvent.isPast && (
                  <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    <FaHourglassHalf className="inline mr-1" />
                    {t('events.otherEvents.comingSoon')}
                  </div>
                )}

                {selectedEvent.isPast && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                    <div className="relative transform -rotate-12" style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))' }}>
                      <div className="relative border-4 border-red-500 bg-red-500/20 backdrop-blur-sm px-8 py-4 rounded-lg">
                        <div className="absolute inset-2 border-2 border-red-500 rounded"></div>
                        <div className="relative z-10">
                          <div className="text-red-500 font-black text-3xl md:text-5xl tracking-widest text-center">{t('events.otherEvents.closed')}</div>
                          <div className="text-red-400 font-bold text-sm md:text-base tracking-wider text-center mt-1">{t('events.otherEvents.eventEnded')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-8 text-white">
                <h2 className={`text-3xl font-bold mb-2 ${selectedEvent.isPast ? 'text-gray-300' : 'text-white'}`}>
                  {selectedEvent.title}
                </h2>
                <h3 className={`text-xl mb-4 ${selectedEvent.isPast ? 'text-gray-400' : 'text-orange-400'}`}>
                  {selectedEvent.subtitle}
                </h3>

                {selectedEvent.isPast && (
                  <div className="bg-red-500/20 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
                    <p className="text-red-300 font-semibold">
                      {t('events.otherEvents.eventEndedMessage')}
                    </p>
                  </div>
                )}

                <div className={`mb-6 leading-relaxed space-y-3 ${selectedEvent.isPast ? 'text-gray-400' : 'text-gray-300'}`}>
                  {selectedEvent.fullDescription.split('\n').map((line, index) => {
                    if (line.trim() === '') {
                      return <div key={index} className="h-2"></div>;
                    } else if (line.startsWith('🔄') || line.startsWith('✅') || line.startsWith('📅') || line.startsWith('🚨') || line.startsWith('🌍')) {
                      return (
                        <h4 key={index} className={`text-lg font-bold mt-4 ${selectedEvent.isPast ? 'text-gray-300' : 'text-orange-400'}`}>
                          {line}
                        </h4>
                      );
                    } else if (line.startsWith('•')) {
                      return (
                        <div key={index} className="flex items-start ml-4">
                          <div className={`w-2 h-2 ${selectedEvent.isPast ? 'bg-gray-500' : 'bg-orange-500'} rounded-full mr-3 mt-2 flex-shrink-0`}></div>
                          <span>{line.substring(2)}</span>
                        </div>
                      );
                    } else if (line.startsWith('👉')) {
                      return (
                        <div key={index} className={`${selectedEvent.isPast ? 'bg-gray-500/20 border-l-4 border-gray-500' : 'bg-orange-500/20 border-l-4 border-orange-500'} p-4 mt-4 rounded-r-lg`}>
                          <span className={`font-semibold ${selectedEvent.isPast ? 'text-gray-300' : 'text-orange-300'}`}>{line}</span>
                        </div>
                      );
                    } else if (line.includes('📍') || line.includes('💰') || line.includes('🕒')) {
                      return (
                        <div key={index} className="bg-white/10 rounded-lg p-3 my-2">
                          <span className="font-semibold">{line}</span>
                        </div>
                      );
                    } else {
                      return (
                        <p key={index} className={selectedEvent.isPast ? 'text-gray-400' : 'text-gray-300'}>
                          {line}
                        </p>
                      );
                    }
                  })}
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <FaMapMarkerAlt className={`${selectedEvent.isPast ? 'text-gray-400' : 'text-orange-500'} mr-2`} />
                      <span className="font-bold">{t('events.otherEvents.location')}</span>
                    </div>
                    <span className={selectedEvent.isPast ? 'text-gray-400' : 'text-gray-300'}>{selectedEvent.location}</span>
                  </div>

                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <FaEuroSign className={`${selectedEvent.isPast ? 'text-gray-400' : 'text-orange-500'} mr-2`} />
                      <span className="font-bold">{t('events.otherEvents.investment')}</span>
                    </div>
                    <span className={selectedEvent.isPast ? 'text-gray-400' : 'text-gray-300'}>{selectedEvent.price}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className={`text-lg font-bold mb-3 ${selectedEvent.isPast ? 'text-gray-300' : 'text-orange-400'}`}>{t('events.otherEvents.whatsIncluded')}</h4>
                  <ul className="space-y-2">
                    {selectedEvent.includes.map((item, index) => (
                      <li key={index} className={`flex items-center ${selectedEvent.isPast ? 'text-gray-400' : 'text-gray-300'}`}>
                        <div className={`w-2 h-2 ${selectedEvent.isPast ? 'bg-gray-500' : 'bg-orange-500'} rounded-full mr-3`}></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {selectedEvent.isPast || selectedEvent.isSoldOut ? (
                  <div className="space-y-4">
                    <button
                      disabled
                      className="w-full bg-gray-600 text-gray-300 py-4 px-6 rounded-xl font-bold text-lg cursor-not-allowed flex items-center justify-center"
                    >
                      <FaBan className="mr-3" />
                      {t('events.otherEvents.eventClosed')}
                    </button>
                    <p className="text-center text-gray-400 text-sm">
                      {t('events.otherEvents.eventEndedMessage')}
                    </p>
                  </div>
                ) : selectedEvent.isTunnelJam || selectedEvent.isProFlyer ? (
                  <button
                    onClick={() => openEventForm(selectedEvent)}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-orange-500/30 transition-all transform hover:scale-[1.02] flex items-center justify-center"
                  >
                    <FaExternalLinkAlt className="mr-3" />
                    {t('events.otherEvents.registerNow')}
                  </button>
                ) : (
                  <button
                    onClick={() => openGoogleForm(selectedEvent.googleFormUrl)}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-orange-500/30 transition-all transform hover:scale-[1.02] flex items-center justify-center"
                  >
                    <FaExternalLinkAlt className="mr-3" />
                    {selectedEvent.isComingSoon ? t('events.otherEvents.getNotified') : t('events.otherEvents.registerButton')}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventsPage;
